import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import {
  getSafeErrorCategory,
  getSafeFirebaseErrorCode,
  isAuthRecoveryTimeout,
  logAuthDiagnostic,
  withAuthRecoveryTimeout,
} from '../utils/authRecovery';

export function normalizeTenantId(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

export function resolveTenantIdFromUserDoc(userDocData) {
  return (
    normalizeTenantId(userDocData?.instanceId) || normalizeTenantId(userDocData?.companyId) || ''
  );
}

export function deriveMembershipCompanyIds(membershipDocs) {
  const ids = [];
  membershipDocs.forEach((doc) => {
    const companyId = doc?.ref?.parent?.parent?.id;
    if (typeof companyId === 'string' && companyId.trim()) {
      ids.push(companyId.trim());
    }
  });
  const unique = Array.from(new Set(ids));
  unique.sort();
  return unique;
}

export function pickDeterministicCompanyId(companyIds) {
  if (!Array.isArray(companyIds) || companyIds.length === 0) return '';
  return companyIds[0] || '';
}

export function useTenantResolution({ currentUser, userDocData, userDocLoading }) {
  const baseTenantId = useMemo(() => resolveTenantIdFromUserDoc(userDocData), [userDocData]);
  const [membershipCompanyIds, setMembershipCompanyIds] = useState([]);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipTimedOut, setMembershipTimedOut] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!currentUser?.uid) {
      setMembershipCompanyIds([]);
      setMembershipLoading(false);
      setMembershipTimedOut(false);
      return () => {
        isMounted = false;
      };
    }

    if (userDocLoading) {
      setMembershipLoading(true);
      setMembershipTimedOut(false);
      return () => {
        isMounted = false;
      };
    }

    if (!baseTenantId) {
      setMembershipCompanyIds([]);
      setMembershipLoading(false);
      setMembershipTimedOut(false);
      return () => {
        isMounted = false;
      };
    }

    setMembershipLoading(true);
    setMembershipTimedOut(false);
    (async () => {
      try {
        logAuthDiagnostic('workspace.membership.start', { source: 'useTenantResolution' });
        const membershipRef = doc(db, 'companies', baseTenantId, 'users', currentUser.uid);
        const snapshot = await withAuthRecoveryTimeout(
          getDoc(membershipRef),
          'workspace.membership.get_doc',
        );
        if (!isMounted) return;
        setMembershipCompanyIds(snapshot.exists() ? [baseTenantId] : []);
        logAuthDiagnostic('workspace.membership.success', {
          source: 'useTenantResolution',
          exists: snapshot.exists(),
        });
      } catch (error) {
        logAuthDiagnostic(
          isAuthRecoveryTimeout(error)
            ? 'workspace.membership.timeout'
            : 'workspace.membership.failure',
          {
            source: 'useTenantResolution',
            code: getSafeFirebaseErrorCode(error),
            category: getSafeErrorCategory(error),
          },
        );
        console.error('auth_workspace_membership_resolution_failed', {
          errorCategory: isAuthRecoveryTimeout(error) ? 'timeout' : 'membership_resolution_failed',
        });
        if (!isMounted) return;
        setMembershipCompanyIds([]);
        setMembershipTimedOut(isAuthRecoveryTimeout(error));
      } finally {
        if (isMounted) {
          setMembershipLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [baseTenantId, currentUser?.uid, userDocLoading]);

  const tenantId = useMemo(() => {
    if (baseTenantId) return baseTenantId;
    return pickDeterministicCompanyId(membershipCompanyIds);
  }, [baseTenantId, membershipCompanyIds]);

  return {
    tenantId,
    membershipCompanyIds,
    membershipLoading,
    membershipTimedOut,
  };
}
